import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { createInstructor, updateIntructor, deleteInstructor, fetchInstructores, fetchInstructorUsage, reassignInstructor } from '../api';
import { useTenantCode } from './useTenantCode';
import { invalidateAfterReassign } from './invalidations';


// Query hook para obtener instructores
export const useInstructoresQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['instructores', tenantCode],
        queryFn: fetchInstructores
    });
};


  // Mutaciones para crear instructores llamando a la función correspondiente
  export const useCreateInstructorMutation = () => useMutation({
    mutationFn: createInstructor, 
    onSuccess: (data: any) => {
        queryClient.invalidateQueries({ queryKey: ['instructores'] });
        // P6 fase 2 — decir si ha salido la invitación. Sin esto no se sabe si el
        // profesor va a poder entrar o se queda como una etiqueta del horario, que
        // es justo lo que decide el correo.
        toast.success(data?.invitation?.invited
            ? 'Instructor creado. Le hemos enviado una invitación por correo.'
            : 'Instructor creado');
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error creando el instructor');
    },
});


// Mutaciones para actualizar instructores llamando a la función correspondiente
export const useUpdateInstructorMutation = () =>  useMutation({
    mutationFn: updateIntructor,
    onSuccess: (data: any) => {
        queryClient.invalidateQueries({ queryKey: ['instructores'] });
        toast.success(data?.invitation?.invited
            ? 'Instructor actualizado. Le hemos enviado una invitación al correo nuevo.'
            : 'Instructor actualizado');
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error actualizando el instructor');
    },
});


// B1 — uso de un instructor. Solo se pide cuando hay un id: el modal de borrado
// lo lanza al abrirse.
export const useInstructorUsageQuery = (instructorId?: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['instructor-usage', tenantCode, instructorId],
        queryFn: () => fetchInstructorUsage(instructorId as string),
        enabled: Boolean(instructorId),
        // El uso cambia con cada clase que se crea o borra; que no se sirva de
        // caché al reabrir el modal.
        staleTime: 0,
    });
};

// B1 — reasignar las clases futuras de un instructor a otro.
export const useReassignInstructorMutation = () => {
    const tenantCode = useTenantCode();
    return useMutation({
        mutationFn: ({ instructorId, targetId }: { instructorId: string; targetId: string }) =>
            reassignInstructor(instructorId, targetId),
        onSuccess: () => invalidateAfterReassign(tenantCode),
        // Se muestra el mensaje del servidor: ante un choque (409) explica qué
        // clase estorba y en qué fecha, y un error genérico dejaría al admin sin
        // saber por qué no se ha reasignado.
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error reasignando el instructor');
        },
    });
};

// Mutaciones para eliminar instructores llamando a la función correspondiente
// B1: el backend puede archivar en vez de borrar, así que el mensaje sale de la
// respuesta y no de un literal fijo.
export const useDeleteInstructorMutation = () => {
    const tenantCode = useTenantCode();
    return useMutation({
        mutationFn: deleteInstructor,
        onSuccess: (response) => {
            invalidateAfterReassign(tenantCode);
            toast.success(response?.archived ? 'Instructor archivado' : 'Instructor eliminado');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error eliminando el instructor');
        },
    });
};